import { useMutation, useQuery } from "@apollo/client/react";
import {
	CardContent,
	Divider,
	Grid,
	Typography,
	Button,
} from "@material-ui/core";
import Card from "@material-ui/core/Card";
import { pick } from "lodash-es";
import React, { useMemo } from "react";
import {
	NumberInput,
	SimpleForm,
	TextInput,
	Title,
	minValue,
	required,
	useNotify,
} from "react-admin";
import type { GetInventorySettingsQuery } from "~/api/get-inventory-settings-query.generated";
import getInventorySettingsQuery from "~/api/get-inventory-settings-query.graphql";
import type {
	UpdateInventorySettingsMutation,
	UpdateInventorySettingsMutationVariables,
} from "~/api/update-inventory-settings-mutation.generated";
import updateInventorySettingsMutation from "~/api/update-inventory-settings-mutation.graphql";
import disconnectXeroMutation from "~/api/disconnect-xero-mutation.graphql";

function InventorySettings() {
	const notify = useNotify();
	const { data, loading, refetch } = useQuery<GetInventorySettingsQuery>(
		getInventorySettingsQuery,
		{
			onError(e) {
				notify(e.toString(), { type: "error" });
			},
		},
	);

	const inventorySettings = data?.inventorySettings;

	const isXeroConnected = useMemo(() => {
		return inventorySettings?.xeroConfig?.isTokenSetValid;
	}, [inventorySettings]);

	const [updateInventorySettings, { loading: saving }] = useMutation<
		UpdateInventorySettingsMutation,
		UpdateInventorySettingsMutationVariables
	>(updateInventorySettingsMutation, {
		onError(e) {
			notify(e.toString(), { type: "error" });
		},
		onCompleted() {
			notify("Inventory settings updated", "info");
		},
	});

	const [disconnectXero, { loading: disconnecting }] = useMutation(
		disconnectXeroMutation,
		{
			onError(e) {
				notify(e.toString(), { type: "error" });
			},
			async onCompleted() {
				notify("Disconnected from Xero", "info");
				await refetch();
			},
		},
	);

	const handleDisconnect = async () => {
		await disconnectXero();
	};

	const onSubmit = async (variables: any) => {
		// Filter out non-mutable fields from xeroConfig
		const { xeroConfig, ...rest } = variables;
		const filteredXeroConfig = {
			inventoryAssetAccountCode: xeroConfig.inventoryAssetAccountCode || "",
			inventoryAdjustmentAccountCode:
				xeroConfig.inventoryAdjustmentAccountCode || "",
			inventoryCogsAccountCode: xeroConfig.inventoryCogsAccountCode || "",
			inventoryContactName: xeroConfig.inventoryContactName || "",
			alertEmail: xeroConfig.alertEmail || "",
		};

		await updateInventorySettings({
			variables: {
				...rest,
				email: rest.email || "",
				xeroConfig: filteredXeroConfig,
			},
		});
	};

	return (
		<Card>
			<CardContent>
				<Title title="Inventory settings" />
				{loading && <div>Loading...</div>}
				{inventorySettings && (
					<SimpleForm
						initialValues={pick(
							inventorySettings,
							"email",
							"daysToNotifyBeforeRunOut",
							"pastDaysUsedForCalculation",
							"xeroConfig",
						)}
						saving={saving}
						save={onSubmit}
					>
						<TextInput source="email" validate={[required()]} />
						<NumberInput
							source="daysToNotifyBeforeRunOut"
							validate={[required(), minValue(1)]}
						/>
						<NumberInput
							source="pastDaysUsedForCalculation"
							validate={[required(), minValue(1)]}
						/>
						<Typography variant="h5">Xero Integration</Typography>
						{/* Xero Account Codes */}
						<>
							<Divider variant="fullWidth" />
							<br />
							{isXeroConnected ? (
								<>
									<Typography style={{ color: "green" }}>Connected</Typography>
									<Button
										onClick={handleDisconnect}
										color="secondary"
										variant="contained"
										disabled={disconnecting}
										style={{ marginTop: "10px" }}
									>
										{disconnecting
											? "Disconnecting..."
											: "Disconnect from Xero"}
									</Button>
								</>
							) : (
								<>
									<Typography style={{ color: "red" }}>
										Not Connected
									</Typography>
									{inventorySettings?.xeroConfig?.authConsentUrl && (
										<Button
											href={inventorySettings?.xeroConfig?.authConsentUrl}
											color="primary"
											variant="contained"
										>
											Connect to Xero
										</Button>
									)}
								</>
							)}
						</>

						{/* Xero Account Codes */}
						<>
							<br />
							<Typography>Accounts</Typography>
							<Grid container spacing={2}>
								<Grid item xs={4}>
									<TextInput
										label="Inventory Asset Account Code"
										source="xeroConfig.inventoryAssetAccountCode"
										fullWidth
									/>
								</Grid>
								<Grid item xs={4}>
									<TextInput
										label="Inventory Adjustment Account Code"
										source="xeroConfig.inventoryAdjustmentAccountCode"
										fullWidth
									/>
								</Grid>
								<Grid item xs={4}>
									<TextInput
										label="Inventory COGS Account Code"
										source="xeroConfig.inventoryCogsAccountCode"
										fullWidth
									/>
								</Grid>
							</Grid>
						</>

						{/* Xero Contacts */}
						<>
							<Typography>Contacts</Typography>
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<TextInput
										label="Inventory Contact Name"
										source="xeroConfig.inventoryContactName"
										fullWidth
										helpText="Contact to use for generating invoices for quantity adjustments"
									/>
								</Grid>
							</Grid>
						</>

						{/* Xero Alert Email */}
						<>
							<Typography>Sync Alerts</Typography>
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<TextInput
										label="Alert Email"
										source="xeroConfig.alertEmail"
										fullWidth
										helpText="Email address to receive Xero sync notifications"
									/>
								</Grid>
							</Grid>
						</>
					</SimpleForm>
				)}
			</CardContent>
		</Card>
	);
}

export default InventorySettings;
